import React, { useState, useEffect } from 'react';
import LoginForm from './LoginForm';
import Header from './Header';
import MonthlyOverview from './Overview/MonthlyOverview';
import ExpenseForm from './ExpenseForm';
import CategoriesManager from './Categories/CategoriesManager';
import { useUser } from '../providers/user';
import { googleSheetsService } from '../services/google-sheets';
import { getDefaultCurrency, getShowCurrencyPreference, saveCurrency, saveShowCurrencyPreference } from '../utils/currency';

const STORAGE_KEY_ID = 'finance_categories_sheet';
const STORAGE_KEY_NAME = 'finance_categories_sheet_name';
const SPREADSHEET_TITLE = 'Finance Tracker Categories';
const DEFAULT_SHEET_NAME = 'Categories';

const makeId = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `id-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const ExpenseTracker = () => {
  const [showAmounts, setShowAmounts] = useState(false);
  const [currentPage, setCurrentPage] = useState('home');
  const [selectedYear] = useState(2025);
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);
  const [currency, setCurrencyState] = useState(getDefaultCurrency());
  const [showCurrency, setShowCurrencyState] = useState(getShowCurrencyPreference());
  const { isLoggedIn, currentUser } = useUser();

  // Currency setter that also saves to localStorage
  const setCurrency = (currencyCode) => {
    setCurrencyState(currencyCode);
    saveCurrency(currencyCode);
  };

  // Show currency setter that also saves to localStorage
  const setShowCurrency = (show) => {
    setShowCurrencyState(show);
    saveShowCurrencyPreference(show);
  };

  // Load categories on mount
  useEffect(() => {
    const loadCategoriesData = async () => {
      if (!currentUser?.accessToken || categoriesLoaded) return;

      try {
        googleSheetsService.setAccessToken(currentUser.accessToken);

        // Check localStorage for cached ID
        const cachedId = localStorage.getItem(STORAGE_KEY_ID);
        const cachedName = localStorage.getItem(STORAGE_KEY_NAME) || DEFAULT_SHEET_NAME;

        let spreadsheetId = null;
        let sheetName = DEFAULT_SHEET_NAME;

        if (cachedId) {
          const valid = await googleSheetsService.verifySpreadsheet(cachedId);
          if (valid) {
            spreadsheetId = cachedId;
            sheetName = cachedName;
          }
        }

        // If no valid cached ID, search by name
        if (!spreadsheetId) {
          const found = await googleSheetsService.searchSpreadsheetByName(SPREADSHEET_TITLE);
          if (found && found.spreadsheetId) {
            spreadsheetId = found.spreadsheetId;
            sheetName = found.sheetName || DEFAULT_SHEET_NAME;
            localStorage.setItem(STORAGE_KEY_ID, spreadsheetId);
            localStorage.setItem(STORAGE_KEY_NAME, sheetName);
          }
        }

        // If we have a spreadsheet, load the categories
        if (spreadsheetId) {
          const data = await googleSheetsService.getSheetData(spreadsheetId, 'A2:C', sheetName);
          if (data?.values && data.values.length > 0) {
            const loaded = data.values.map((row, index) => ({
              id: makeId(),
              rowNumber: index + 2,
              category: row[0] || '',
              subCategory: row[1] || '',
              budget: parseFloat(row[2]) || 0
            }));
            setCategories(loaded);
          }
        }

        setCategoriesLoaded(true);
      } catch (error) {
        console.error('Error loading categories:', error);
        setCategoriesLoaded(true);
      }
    };

    if (isLoggedIn && currentUser?.accessToken) {
      loadCategoriesData();
    }
  }, [isLoggedIn, currentUser?.accessToken, categoriesLoaded]);

  // Load expenses on mount
  useEffect(() => {
    const loadExpensesData = async () => {
      if (!currentUser?.accessToken || !selectedYear) return;

      try {
        googleSheetsService.setAccessToken(currentUser.accessToken);

        // Get the expense sheet ID from localStorage
        const expenseSheetId = localStorage.getItem(`finance_sheet_${selectedYear}`);

        if (expenseSheetId) {
          const valid = await googleSheetsService.verifySpreadsheet(expenseSheetId);

          if (valid) {
            // Fetch expenses from the sheet
            const response = await googleSheetsService.getSheetData(
              expenseSheetId,
              'A2:G1000'
            );

            if (response.values && response.values.length > 0) {
              const parsedExpenses = response.values
                .filter(row => row[0] && row[1] && row[2]) // Must have date, amount, and category
                .map((row, index) => ({
                  id: `${row[0]}-${index}`,
                  date: row[0],
                  amount: parseFloat(row[1]) || 0,
                  category: row[2],
                  subCategory: row[3] || '',
                  additionalInfo: row[4] || '',
                  month: row[5] || '',
                  year: parseInt(row[6]) || selectedYear
                }))
                .filter(expense => expense.year === selectedYear);

              setExpenses(parsedExpenses);
            }
          }
        }
      } catch (error) {
        console.error('Error loading expenses:', error);
      }
    };

    if (isLoggedIn && currentUser?.accessToken) {
      loadExpensesData();
    }
  }, [isLoggedIn, currentUser?.accessToken, selectedYear]);

  if (!isLoggedIn) {
    return <LoginForm />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="max-w-7xl mx-auto">
        <Header
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          showAmounts={showAmounts}
          setShowAmounts={setShowAmounts}
          setExpenses={setExpenses}
          currency={currency}
          setCurrency={setCurrency}
          showCurrency={showCurrency}
          setShowCurrency={setShowCurrency}
        />

        {currentPage === 'home' && (
          <>
            <ExpenseForm
              setExpenses={setExpenses}
              selectedYear={selectedYear}
              categories={categories}
              currency={currency}
              showCurrency={showCurrency}
              showAmounts={showAmounts}
            />
            <MonthlyOverview
              expenses={expenses}
              selectedYear={selectedYear}
              showAmounts={showAmounts}
              categories={categories}
              currency={currency}
              showCurrency={showCurrency}
            />
          </>
        )}

        {currentPage === 'categories' && (
          <CategoriesManager
            categories={categories}
            setCategories={setCategories}
            selectedYear={selectedYear}
            currency={currency}
            showCurrency={showCurrency}
            showAmounts={showAmounts}
          />
        )}
      </div>
    </div>
  );
};

export default ExpenseTracker;
